import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Select, Space, Statistic, Table, Typography, message } from 'antd';
import { Column, Line, Pie } from '@ant-design/charts';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import { deletePin, fetchPins, reorderPins, previewUdq, type AiPinnedItem } from '@/services/aiAgent.service';

const { Title, Text } = Typography;

const LAYOUT_KEY = 'ai_dashboard_layout_v3';

type DashboardLayout = { tab: string; orderedIds: string[] };

function loadLayout(): DashboardLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (raw) return JSON.parse(raw) as DashboardLayout;
  } catch {
    // ignore
  }
  return { tab: 'default', orderedIds: [] };
}

function saveLayout(layout: DashboardLayout) {
  localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
}

function PinWidget({
  pin,
  onRefresh,
  onRemove,
}: {
  pin: AiPinnedItem;
  onRefresh?: () => void;
  onRemove?: () => void;
}) {
  const { formatCurrency } = useCurrency();
  const payload = pin.payload || {};
  const navigate = useNavigate();

  if (pin.sourceType === 'kpi' && payload.kpi) {
    const kpi = payload.kpi as { label?: string; value?: number | string; format?: string };
    return (
      <Card
        title={pin.title}
        className="ai-viz-zone"
        extra={
          <Space size={4}>
            <Button size="small" onClick={onRefresh}>Refresh</Button>
            <Button size="small" danger onClick={onRemove}>Remove</Button>
          </Space>
        }
      >
        {kpi.format === 'currency' ? (
          <Statistic title={kpi.label} value={formatCurrency(Number(kpi.value))} />
        ) : (
          <Statistic title={kpi.label} value={kpi.value as number} />
        )}
      </Card>
    );
  }

  if (payload.chartSpec) {
    const chart = payload.chartSpec as {
      type?: string;
      data?: Record<string, unknown>[];
      xField?: string;
      yField?: string;
      angleField?: string;
      colorField?: string;
    };
    const data = Array.isArray(chart.data) ? chart.data : [];
    return (
      <Card
        title={pin.title}
        className="ai-viz-zone"
        extra={
          <Space size={4}>
            <Button size="small" onClick={() => navigate('/ai/workspace')}>Open in Workspace</Button>
            <Button size="small" danger onClick={onRemove}>Remove</Button>
          </Space>
        }
      >
        {chart.type === 'line' ? (
          <Line data={data} xField={chart.xField || 'x'} yField={chart.yField || 'y'} height={280} />
        ) : chart.type === 'pie' || chart.type === 'donut' ? (
          <Pie
            data={data}
            angleField={chart.angleField || chart.yField || 'value'}
            colorField={chart.colorField || chart.xField || 'category'}
            innerRadius={chart.type === 'donut' ? 0.6 : 0}
            height={280}
          />
        ) : (
          <Column data={data} xField={chart.xField || 'x'} yField={chart.yField || 'y'} height={280} />
        )}
      </Card>
    );
  }

  if (pin.sourceType === 'table' && payload.dataTable) {
    const table = payload.dataTable as {
      columns?: Array<{ key: string; label: string }>;
      rows?: Record<string, unknown>[];
    };
    return (
      <Card title={pin.title} className="ai-viz-zone" extra={<Button size="small" danger onClick={onRemove}>Remove</Button>}>
        <Table
          size="small"
          pagination={{ pageSize: 8 }}
          dataSource={table.rows || []}
          rowKey={(_, i) => String(i)}
          columns={(table.columns || []).map((c) => ({ title: c.label, dataIndex: c.key, key: c.key }))}
          scroll={{ x: true }}
        />
      </Card>
    );
  }

  return (
    <Card title={pin.title} className="ai-viz-zone">
      <Text type="secondary">{pin.sourceType}</Text>
    </Card>
  );
}

export default function AiDashboardPage() {
  const [layout, setLayout] = useState<DashboardLayout>(loadLayout);
  const navigate = useNavigate();

  const pinsQ = useQuery({
    queryKey: ['ai-dashboard-pins', layout.tab],
    queryFn: () => fetchPins(layout.tab),
    staleTime: 30_000,
  });

  const pins = pinsQ.data || [];

  useEffect(() => {
    if (!layout.orderedIds.length && pins.length) {
      const next = { ...layout, orderedIds: pins.map((p) => p.id) };
      setLayout(next);
      saveLayout(next);
    }
  }, [pins, layout]);

  const orderedPins = useMemo(() => {
    const map = new Map(pins.map((p) => [p.id, p]));
    const ordered = layout.orderedIds.map((id) => map.get(id)).filter(Boolean) as AiPinnedItem[];
    const rest = pins.filter((p) => !layout.orderedIds.includes(p.id));
    return [...ordered, ...rest];
  }, [layout.orderedIds, pins]);

  const movePin = async (id: string, dir: -1 | 1) => {
    const ids = orderedPins.map((p) => p.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    const next = { ...layout, orderedIds: ids };
    setLayout(next);
    saveLayout(next);
    await reorderPins(ids);
  };

  const handleRefresh = useCallback(async (pin: AiPinnedItem) => {
    const intent = typeof pin.payload?.intent === 'string' ? pin.payload.intent : undefined;
    const reportId = typeof pin.payload?.reportId === 'string' ? pin.payload.reportId : undefined;
    if (reportId) {
      navigate(`/ai/udq/${reportId}`);
      return;
    }
    if (!intent) {
      message.info('No saved intent to refresh');
      return;
    }
    const hide = message.loading('Refreshing…', 0);
    try {
      const filters =
        pin.payload?.filters && typeof pin.payload.filters === 'object'
          ? (pin.payload.filters as Record<string, unknown>)
          : {};
      const out = await previewUdq({ intent, filters });
      message.success(out.result.answerText || 'Refreshed');
      void pinsQ.refetch();
    } catch {
      message.error('Refresh failed');
    } finally {
      hide();
    }
  }, [navigate, pinsQ]);

  return (
    <div className="ai-workspace-main" style={{ padding: 24, overflow: 'auto' }}>
      <Space style={{ marginBottom: 20, width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          My AI Dashboard
        </Title>
        <Space>
          <Select
            value={layout.tab}
            style={{ width: 160 }}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'executive', label: 'Executive' },
              { value: 'operations', label: 'Operations' },
            ]}
            onChange={(tab) => {
              const next = { tab, orderedIds: [] as string[] };
              setLayout(next);
              saveLayout(next);
            }}
          />
          <Link to="/ai/workspace">
            <Button type="primary">Open AI Workspace</Button>
          </Link>
        </Space>
      </Space>
      {orderedPins.length === 0 && !pinsQ.isLoading ? (
        <Empty description="Pin charts and KPIs from the AI Workspace to build your dashboard.">
          <Link to="/ai/workspace">
            <Button type="primary">Go to Workspace</Button>
          </Link>
        </Empty>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20 }}>
          {orderedPins.map((pin) => (
            <div key={pin.id}>
              <Space size={4} style={{ marginBottom: 6 }}>
                <Button size="small" onClick={() => void movePin(pin.id, -1)} aria-label="Move up">
                  ↑
                </Button>
                <Button size="small" onClick={() => void movePin(pin.id, 1)} aria-label="Move down">
                  ↓
                </Button>
              </Space>
              <PinWidget
                pin={pin}
                onRefresh={() => void handleRefresh(pin)}
                onRemove={() => void deletePin(pin.id).then(() => pinsQ.refetch())}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
